import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '全部';

  const filteredArticles = useMemo(() => {
    if (activeSlug === 'all') return [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return articles.filter(a => a.category === activeSlug).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [activeSlug]);

  const displayCategories = categories.filter(c => c.slug !== 'all');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `佐山爱${categoryName} - 佐山爱粉丝站`,
    description: `浏览佐山爱的${categoryName}相关内容，了解最新资讯。`,
    url: `https://chengtuitui.cn/category/${activeSlug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main>
        <div className="pt-20 pb-0 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6 mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                佐山爱{categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {filteredArticles.length} 篇相关内容
              </p>
            </div>

            <div className="flex items-center gap-2 pb-4 overflow-x-auto flex-wrap border-b border-background-200/70 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-label transition-colors ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer"
                  >
                    <div className="relative w-full h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full bg-background-50/85 backdrop-blur-sm text-xs font-label text-foreground-700">
                          {article.category === 'profile' ? '个人资料' :
                           article.category === 'gravure' ? '写真作品' :
                           article.category === 'film' ? '影视作品' :
                           article.category === 'news' ? '最新动态' : '访谈专访'}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2 leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i> {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}